/**
 * LandingPage — unauthenticated lox247 marketing homepage.
 *
 * Wrapped in <PublicLayout /> (App.tsx) which provides the header + footer.
 * Do NOT add a `<header>` element here.
 */
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useSession } from '@/client/lib/auth'
import { Key, MapPin, ShieldCheck, Truck } from 'lucide-react'

export function LandingPage() {
  const { data: session } = useSession()
  const isAuthed = !!session?.user

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="border-b border-border">
        <div className="container mx-auto max-w-5xl px-4 py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Wholesale locksmith supplies, Newcastle and Lake Macquarie.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              Trade wholesale of locksmith parts, key blanks, and machinery from Gordon
              Heap (lox247). Curated stock, technical advice from a working locksmith,
              local pickup or delivery.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {isAuthed ? (
                <Button asChild size="lg">
                  <Link to="/catalogue">Browse catalogue</Link>
                </Button>
              ) : (
                <Button asChild size="lg">
                  <Link to="/sign-in">Trade sign in</Link>
                </Button>
              )}
              <Button asChild variant="outline" size="lg">
                <a href="#about">Who we serve</a>
              </Button>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Trade-only. Sign in with Google. Access approved by Gordon.
            </p>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="border-b border-border">
        <div className="container mx-auto max-w-5xl px-4 py-12 md:py-16">
          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <CardContent className="pt-6">
                <Key className="h-6 w-6 text-primary" />
                <h2 className="mt-4 font-semibold">Curated stock</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Key blanks, parts, and machinery selected by a working locksmith. Not
                  a generic catalogue.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <MapPin className="h-6 w-6 text-primary" />
                <h2 className="mt-4 font-semibold">Local fulfilment</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Newcastle and Lake Macquarie pickup or local delivery. No long-haul
                  shipping.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <ShieldCheck className="h-6 w-6 text-primary" />
                <h2 className="mt-4 font-semibold">Trade-only access</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Approved trade buyers only. Submit an order, Gordon confirms and
                  invoices.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="border-b border-border">
        <div className="container mx-auto max-w-3xl px-4 py-12 md:py-16">
          <h2 className="text-2xl md:text-3xl font-bold">About lox247</h2>
          <div className="mt-6 space-y-4 text-base text-muted-foreground leading-relaxed">
            <p>
              Gordon Heap is the former owner of Ambassador Locksmiths in Newcastle
              NSW. After selling the business, he kept his stock of parts, key blanks,
              and locksmith machinery, and now sells it direct to a small circle of
              trade locksmiths.
            </p>
            <p>
              lox247 is the catalogue. Sign in, browse what's in stock, submit an order
              and Gordon will confirm it and send an invoice. Pickup in Newcastle or
              delivery around Lake Macquarie.
            </p>
            <p>
              Not a trade locksmith? This catalogue isn't for retail. Get in touch with
              your local locksmith.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="container mx-auto max-w-3xl px-4 py-12 md:py-16 text-center">
          <Truck className="h-8 w-8 text-primary mx-auto" />
          <h2 className="mt-4 text-2xl md:text-3xl font-bold">Ready to browse?</h2>
          <p className="mt-3 text-muted-foreground">
            Sign in with your Google account. Gordon will approve your access.
          </p>
          <div className="mt-6">
            {isAuthed ? (
              <Button asChild size="lg">
                <Link to="/catalogue">Open catalogue</Link>
              </Button>
            ) : (
              <Button asChild size="lg">
                <Link to="/sign-in">Trade sign in</Link>
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
